import { createFileRoute, redirect } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { CONTENT_PROVIDERS } from '~/features/content/types/content.types';
import { JOB_STATES, RUN_TYPES } from '~/features/content/utils/content-jobs';

const ContentRunsPage = lazy(() => import('~/features/content/pages/content-runs-page'));

// Filters live in the URL, so a back link or a shared link lands on the same list. The prompt /
// preset keys are one-shot: they open "Start a run" prefilled, then are cleared.
const searchSchema = z.object({
  type: z.enum(RUN_TYPES).optional().catch(undefined),
  state: z.enum(JOB_STATES).optional().catch(undefined),
  channel: z.enum(CONTENT_PROVIDERS).optional().catch(undefined),
  q: z.string().max(200).optional().catch(undefined),
  // The Archived view: runs hidden from Runs (nothing is deleted).
  view: z.enum(['archived']).optional().catch(undefined),
  // The old QC checks / Catalog runs tab — mapped to `type` in `beforeLoad`, so old links still land.
  tab: z.enum(RUN_TYPES).optional().catch(undefined),
  // "Use in a new run" from AI instructions — pins that instructions version in the run.
  promptFlow: z.string().optional().catch(undefined),
  promptVersion: z.string().optional().catch(undefined),
  promptVertical: z.string().optional().catch(undefined),
  promptPlatform: z.string().optional().catch(undefined),
  // Opens "Start a run" on a template — set by Review's empty state and the Quality tab.
  preset: z.enum(['attribution-review', 'score']).optional().catch(undefined),
});

/** Runs at `/content/runs` (the Content landing) — QC checks and catalog runs in one list. */
export const Route = createFileRoute('/_authenticated/_tenant/content/runs/')({
  // The Content layout already checks the screen permission.
  validateSearch: searchSchema,
  beforeLoad: ({ search }) => {
    if (!search.tab) return;
    const { tab, ...rest } = search;
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router requires throwing a redirect
    throw redirect({
      to: '/content/runs',
      search: { ...rest, type: rest.type ?? tab },
      replace: true,
    });
  },
  component: RunsRoute,
});

function RunsRoute() {
  const search = Route.useSearch();
  const { promptFlow, promptVersion, promptVertical, promptPlatform } = search;
  const prompt =
    promptFlow && promptVersion
      ? {
          flow: promptFlow,
          version: promptVersion,
          ...(promptVertical && { vertical: promptVertical }),
          ...(promptPlatform && { platform: promptPlatform }),
        }
      : undefined;
  return (
    <Suspense fallback={<PageSkeleton />}>
      <ContentRunsPage
        filters={{
          type: search.type,
          state: search.state,
          channel: search.channel,
          q: search.q,
          view: search.view,
        }}
        prompt={prompt}
        preset={search.preset}
      />
    </Suspense>
  );
}
